// src/pages/Events/components/SmartSearchPanel.jsx
// 🔍 Smart search panel with camera selection and filters

import { FiCalendar, FiClock, FiFilter, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useTheme } from "../../../context/ThemeContext";

export default function SmartSearchPanel({
  cameras = [],
  selectedCameras = [],
  onSelectCamera,
  searchDate,
  setSearchDate,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  onApplyPreset,
  eventTypeFilter,
  setEventTypeFilter,
}) {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const shiftDate = (delta) => {
    const d = new Date(searchDate);
    d.setDate(d.getDate() + delta);
    setSearchDate(d.toISOString().slice(0, 10));
  };

  return (
    <div className="p-4 space-y-6">
      {/* ===== HEADER ===== */}
      <div>
        <h2 className="text-lg font-bold mb-1">Search & Filters</h2>
        <p className="text-xs text-gray-500">Select cameras and time range</p>
      </div>

      {/* ===== CAMERA LIST ===== */}
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <FiFilter className="w-4 h-4" />
          Cameras ({cameras.length})
        </h3>

        <div className="space-y-2 max-h-64 overflow-y-auto">
          {cameras.map(camera => (
            <button
              key={camera.id}
              onClick={() => onSelectCamera(camera)}
              className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                selectedCameras.find(c => c.id === camera.id)
                  ? "border-blue-600 bg-blue-600/10"
                  : isDark
                  ? "border-neutral-700 hover:border-neutral-600"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="font-medium text-sm">{camera.name}</div>
              {camera.location && (
                <div className="text-xs text-gray-500 mt-1">{camera.location}</div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ===== DATE PICKER ===== */}
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <FiCalendar className="w-4 h-4" />
          Date
        </h3>

        <div className="flex items-center gap-2">
          <button
            onClick={() => shiftDate(-1)}
            className={`p-2 rounded border ${
              isDark
                ? "border-neutral-700 hover:bg-neutral-700"
                : "border-gray-300 hover:bg-gray-100"
            }`}
          >
            <FiChevronLeft className="w-4 h-4" />
          </button>

          <input
            type="date"
            value={searchDate}
            onChange={(e) => setSearchDate(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            className={`flex-1 px-3 py-2 rounded border ${
              isDark
                ? "bg-neutral-800 border-neutral-700"
                : "bg-white border-gray-300"
            }`}
          />

          <button
            onClick={() => shiftDate(1)}
            disabled={searchDate >= new Date().toISOString().slice(0, 10)}
            className={`p-2 rounded border ${
              isDark
                ? "border-neutral-700 hover:bg-neutral-700 disabled:opacity-30"
                : "border-gray-300 hover:bg-gray-100 disabled:opacity-30"
            }`}
          >
            <FiChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ===== QUICK PRESETS ===== */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Quick Presets</h3>

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => onApplyPreset("last-hour")}
            className="px-3 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
          >
            Last Hour
          </button>
          <button
            onClick={() => onApplyPreset("last-4h")}
            className="px-3 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
          >
            Last 4h
          </button>
          <button
            onClick={() => onApplyPreset("today")}
            className="px-3 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
          >
            Today
          </button>
          <button
            onClick={() => onApplyPreset("yesterday")}
            className="px-3 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
          >
            Yesterday
          </button>
        </div>
      </div>

      {/* ===== TIME RANGE ===== */}
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <FiClock className="w-4 h-4" />
          Time Range
        </h3>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Start Time</label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className={`w-full px-3 py-2 rounded border ${
                isDark
                  ? "bg-neutral-800 border-neutral-700"
                  : "bg-white border-gray-300"
              }`}
            />
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">End Time</label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className={`w-full px-3 py-2 rounded border ${
                isDark
                  ? "bg-neutral-800 border-neutral-700"
                  : "bg-white border-gray-300"
              }`}
            />
          </div>
        </div>
      </div>

      {/* ===== EVENT TYPE FILTER ===== */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Event Type</h3>

        <select
          value={eventTypeFilter}
          onChange={(e) => setEventTypeFilter(e.target.value)}
          className={`w-full px-3 py-2 rounded border ${
            isDark
              ? "bg-neutral-800 border-neutral-700"
              : "bg-white border-gray-300"
          }`}
        >
          <option value="all">All Events</option>
          <option value="motion">Motion Only</option>
          <option value="analytics">Analytics Only</option>
        </select>
      </div>

      {/* ===== INFO ===== */}
      <div className={`p-3 rounded text-xs ${
        isDark ? "bg-blue-900/20 text-blue-400" : "bg-blue-50 text-blue-700"
      }`}>
        💡 Tip: Search is limited to 2 hours for optimal performance
      </div>
    </div>
  );
}
